import React, { useMemo, useState } from 'react';
import { StyleSheet, View, TextInput, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';
import { BORDER_RADIUS } from '@/constants/config';
import { getMemoryThemeColors } from './memoryTheme';

const isWeb = Platform.OS === 'web';

/**
 * MemoryFilterTabs
 *
 * Controls for:
 * - Search query input
 * - Segmented filter tabs (All, Learned, Manual, Recent) with counter badges
 * - Sort order toggle
 * - Select mode toggle
 */
function MemoryFilterTabs({
  searchQuery,
  onSearchChange,
  activeFilter, // 'all' | 'learned' | 'manual' | 'recent'
  onFilterChange,
  counts = { all: 0, learned: 0, manual: 0, recent: 0 },
  sortOrder, // 'newest' | 'oldest' | 'alphabetical'
  onSortChange,
  resultCount = 0,
  selectionMode,
  onToggleSelectionMode,
}) {
  const { colors, isDark } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);

  const memColors = useMemo(() => getMemoryThemeColors(isDark, colors), [isDark, colors]);

  const styles = useMemo(
    () => createStyles(colors, memColors, isDark, isXS, isSM),
    [colors, memColors, isDark, isXS, isSM]
  );

  const filters = useMemo(() => [
    { id: 'all', label: t('pages.memory.filterAll'), count: counts.all, icon: 'list-check' },
    { id: 'learned', label: t('pages.memory.filterLearned'), count: counts.learned, icon: 'sparkles' },
    { id: 'manual', label: t('pages.memory.filterManual'), count: counts.manual, icon: 'user' },
    { id: 'recent', label: t('pages.memory.filterRecent'), count: counts.recent, icon: 'clock-rotate-left' },
  ], [counts, t]);

  const sortOptions = [
    { id: 'newest', label: t('pages.common.newestFirst') },
    { id: 'oldest', label: t('pages.common.oldestFirst') },
    { id: 'alphabetical', label: t('pages.memory.sortAlphabetical') },
  ];

  return (
    <View style={styles.container}>
      <View style={styles.listHeading}>
        <Text tag="h2" size="m" weight="bold">{t('pages.memory.savedMemories')}</Text>
        <Text size="xs" color="secondary" accessibilityLiveRegion="polite">
          {t('pages.memory.resultCount', { count: resultCount, total: counts.all })}
        </Text>
      </View>
      {/* Search Bar */}
      <View style={styles.searchRow}>
        <View style={[styles.searchWrapper, isFocused && { borderColor: colors.text_color_primary }]}>
          <Icon name="magnifying-glass" size={14} color={colors.text_color_secondary} />
          <TextInput
            style={styles.searchInput}
            accessibilityLabel={t('pages.memory.searchPlaceholder')}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            value={searchQuery}
            onChangeText={onSearchChange}
            placeholder={t('pages.memory.searchPlaceholder')}
            placeholderTextColor={colors.color_input_placeholder || colors.text_color_tertiary}
          />
          {searchQuery.length > 0 && (
            <Pressable
              onPress={() => onSearchChange('')}
              style={styles.clearSearchBtn}
              accessibilityRole="button"
              accessibilityLabel="Clear search"
            >
              <Icon name="xmark" size={12} color={colors.text_color_secondary} />
            </Pressable>
          )}
        </View>

        {/* Sort and Select Buttons on desktop/tablet */}
        <View style={styles.toolsRow}>
          <CatalogSortDropdown
            value={sortOrder}
            options={sortOptions}
            onChange={onSortChange}
            label={t('sidebar.sort')}
            size="medium"
          />

          <Pressable
            style={({ hovered }) => [
              styles.toolBtn,
              selectionMode && styles.toolBtnActive,
              hovered && !selectionMode && styles.toolBtnHovered,
            ]}
            onPress={onToggleSelectionMode}
            accessibilityRole="button"
            accessibilityLabel={selectionMode ? t('pages.memory.clearSelection') : t('common.select')}
          >
            <Icon
              name={selectionMode ? 'xmark' : 'square-check'}
              size={13}
              color={selectionMode ? colors.text_color_primary : colors.text_color_secondary}
            />
            <Text
              size="xs"
              weight={selectionMode ? 'bold' : 'medium'}
              color="primary"
            >
              {selectionMode ? t('pages.memory.clearSelection') : t('common.select')}
            </Text>
          </Pressable>
        </View>
      </View>

      {/* Filter Tabs */}
      <View style={styles.tabsRow}>
        {filters.map((f) => {
          const isActive = activeFilter === f.id;
          return (
            <Pressable
              key={f.id}
              style={({ hovered }) => [
                styles.tabPill,
                isActive && styles.tabPillActive,
                hovered && !isActive && styles.tabPillHovered,
              ]}
              onPress={() => onFilterChange(f.id)}
              accessibilityRole="button"
              accessibilityState={{ selected: isActive }}
              {...(isWeb ? { 'aria-pressed': isActive } : {})}
              accessibilityLabel={`${f.label} (${f.count})`}
            >
              <Icon
                name={f.icon}
                size={12}
                color={isActive ? colors.text_color_primary : colors.text_color_secondary}
              />
              <Text
                size="xs"
                weight={isActive ? 'bold' : 'medium'}
                color={isActive ? 'primary' : 'secondary'}
              >
                {f.label}
              </Text>
              <View style={[styles.tabBadge, isActive && styles.tabBadgeActive]}>
                <Text
                  size="xxs"
                  weight="semibold"
                  color={isActive ? 'primary' : 'secondary'}
                >
                  {f.count}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const createStyles = (colors, memColors, isDark, isXS, isSM) =>
  StyleSheet.create({
    listHeading: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
    container: {
      gap: 12,
      marginBottom: 16,
    },
    searchRow: {
      flexDirection: isXS ? 'column' : 'row',
      alignItems: isXS ? 'stretch' : 'center',
      gap: 10,
    },
    searchWrapper: {
      flex: isXS ? undefined : 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      backgroundColor: colors.solid_color_secondary,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      borderRadius: BORDER_RADIUS.medium,
      paddingHorizontal: 12,
      height: 42,
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      fontSize: 14,
      color: colors.text_color_primary,
      height: '100%',
      ...(isWeb ? { outlineStyle: 'none' } : {}),
    },
    clearSearchBtn: {
      padding: 6,
      borderRadius: 12,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    toolsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      justifyContent: isXS ? 'flex-end' : 'flex-start',
    },
    toolBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_secondary,
      height: 42,
      ...(isWeb ? { cursor: 'pointer', transition: 'all 0.16s ease' } : {}),
    },
    toolBtnHovered: {
      borderColor: colors.border_color_input,
      backgroundColor: colors.solid_color_tertiary,
    },
    toolBtnActive: {
      borderColor: colors.text_color_primary,
      backgroundColor: colors.solid_color_tertiary,
    },
    tabsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      alignItems: 'center',
    },
    tabPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      minHeight: 40,
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      ...(isWeb ? { cursor: 'pointer', transition: 'all 0.16s ease' } : {}),
    },
    tabPillHovered: {
      borderColor: colors.border_color_input,
      backgroundColor: colors.solid_color_secondary,
    },
    tabPillActive: {
      borderColor: colors.text_color_primary,
      backgroundColor: colors.solid_color_tertiary,
    },
    tabBadge: {
      paddingHorizontal: 6,
      paddingVertical: 1,
      borderRadius: 10,
      backgroundColor: colors.solid_color_tertiary,
    },
    tabBadgeActive: {
      backgroundColor: colors.solid_color_block,
    },
  });

export default React.memo(MemoryFilterTabs);
